export type ApiResponse<T> = { success: boolean; data: T; code?: string; message?: string; error?: string }
export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'
export type RequestOptions = { url: string; method?: RequestMethod; data?: unknown; accessToken?: string | null; auth?: boolean; skipRefresh?: boolean; timeout?: number; header?: Record<string, string>; responseType?: 'json' | 'blob' }
export type AuthSession = { accessToken: string; refreshToken: string; user: AuthUser }
export type AuthUser = { id: string; name: string; avatarUrl?: string; tenantId: string; organizationId: string; roleGroup: 'LAWYER' | 'NORMAL_USER'; roleCodes: string[] }
type RefreshResult = { accessToken: string; refreshToken: string; session: { userId: string; tenantId: string; username: string; displayName?: string | null; avatarUrl?: string | null; organizationId?: string | null } }

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || window.location.origin).replace(/\/$/, '')
export const CHANNEL_CODE = import.meta.env.VITE_CHANNEL_CODE || 'pwa-zhaoyou'
let runtimeChannelCode = CHANNEL_CODE
export const getChannelCode = () => runtimeChannelCode
export const setChannelCode = (channelCode: string) => { if (channelCode.trim()) runtimeChannelCode = channelCode.trim() }
export const CHANNEL_HEADER = 'X-Monstera-Channel-Code'
export const TERMINAL_ID_HEADER = 'X-Monstera-Terminal-Id'
const STORAGE_KEY = 'monstera_pwa_auth_session'
const TERMINAL_ID_STORAGE_KEY = 'monstera_pwa_terminal_id'
let refreshInFlight: Promise<AuthSession> | null = null

export class ApiRequestError extends Error {
  constructor(message: string, public readonly url: string, public readonly statusCode?: number) { super(message); this.name = 'ApiRequestError' }
}

export function isUnauthorizedError(error: unknown): error is ApiRequestError { return error instanceof ApiRequestError && error.statusCode === 401 }

export const tokenManager = {
  getSession(): AuthSession | null {
    try { const value = localStorage.getItem(STORAGE_KEY); const session = value ? JSON.parse(value) as unknown : null; return isAuthSession(session) ? session : null } catch { return null }
  },
  getAccessToken() { return this.getSession()?.accessToken || null },
  getRefreshToken() { return this.getSession()?.refreshToken || null },
  setSession(session: AuthSession) { localStorage.setItem(STORAGE_KEY, JSON.stringify(session)) },
  clearSession() { localStorage.removeItem(STORAGE_KEY); window.dispatchEvent(new Event('monstera:auth-cleared')) }
}

export async function request<T>(options: RequestOptions): Promise<T> {
  async function perform<R>(input: RequestOptions, retrying = false): Promise<R> {
    const url = toFullUrl(input.url)
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), input.timeout || 30000)
    const headers: Record<string, string> = { ...(input.header || {}), [CHANNEL_HEADER]: getChannelCode(), [TERMINAL_ID_HEADER]: getTerminalId() }
    const accessToken = input.accessToken || tokenManager.getAccessToken()
    if (input.auth !== false && accessToken) headers.Authorization = `Bearer ${accessToken}`
    const isFormData = input.data instanceof FormData
    const requestBody = input.data === undefined ? undefined : isFormData ? input.data as FormData : JSON.stringify(input.data)
    if (input.data !== undefined && !isFormData) headers['Content-Type'] = 'application/json'
    let response: Response
    try {
      response = await fetch(url, { method: input.method || 'GET', headers, body: requestBody, signal: controller.signal })
    } catch (error) {
      throw new ApiRequestError(error instanceof Error ? error.message : 'network error', url)
    } finally { window.clearTimeout(timer) }
    if (response.status === 401) {
      const body = await readBody<R>(response)
      const unauthorized = new ApiRequestError(errorMessage(body, `request failed: ${response.status}`), url, 401)
      if (!retrying && input.auth !== false && !input.skipRefresh) {
        try {
          const session = await refresh()
          return perform<R>({ ...input, accessToken: session.accessToken }, true)
        } catch { tokenManager.clearSession() }
      }
      throw unauthorized
    }
    if (input.responseType === 'blob') {
      if (!response.ok) throw new ApiRequestError(`request failed: ${response.status}`, url, response.status)
      return await response.blob() as R
    }
    const body = await readBody<R>(response)
    if (!response.ok || !body?.success) throw new ApiRequestError(errorMessage(body, `request failed: ${response.status}`), url, response.status)
    return body.data as R
  }
  async function refresh() {
    if (refreshInFlight) return refreshInFlight
    const refreshToken = tokenManager.getRefreshToken()
    if (!refreshToken) throw new ApiRequestError('missing refresh token', toFullUrl('/api/auth/refresh'), 401)
    refreshInFlight = perform<RefreshResult>({ url: '/api/auth/refresh', method: 'POST', data: { refreshToken }, auth: false, skipRefresh: true }, true).then((result) => {
      const previousUser = tokenManager.getSession()?.user
      if (!previousUser) throw new ApiRequestError('missing user session', toFullUrl('/api/auth/refresh'), 401)
      const session: AuthSession = { accessToken: result.accessToken, refreshToken: result.refreshToken, user: { ...previousUser, id: result.session.userId, tenantId: result.session.tenantId, organizationId: result.session.organizationId || '', name: result.session.displayName || result.session.username, avatarUrl: result.session.avatarUrl || '' } }
      tokenManager.setSession(session)
      return session
    }).finally(() => { refreshInFlight = null })
    return refreshInFlight
  }
  return perform<T>(options)
}

async function readBody<T>(response: Response): Promise<ApiResponse<T> | undefined> { try { return await response.json() as ApiResponse<T> } catch { return undefined } }
function errorMessage<T>(body: ApiResponse<T> | undefined, fallback: string) { return body?.message || body?.error || body?.code || fallback }
function toFullUrl(url: string) { return /^https?:\/\//i.test(url) ? url : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}` }
export function resolveApiUrl(url: string) { return toFullUrl(url) }
function isAuthSession(value: unknown): value is AuthSession { if (!value || typeof value !== 'object') return false; const session = value as Partial<AuthSession>; return typeof session.accessToken === 'string' && Boolean(session.accessToken) && typeof session.refreshToken === 'string' && Boolean(session.refreshToken) && Boolean(session.user && typeof session.user.id === 'string' && typeof session.user.name === 'string') }
function getTerminalId() { let value = localStorage.getItem(TERMINAL_ID_STORAGE_KEY); if (!value) { value = crypto.randomUUID(); localStorage.setItem(TERMINAL_ID_STORAGE_KEY, value) } return value }
